import React, { useCallback, useEffect } from "react";
import DynamicFieldsGrid, { renderPaymentItem } from "./DynamicFieldsGrid";
import ControlledField from "./ControlledField";
import FloatingNumber from "../../../../components/input/FloatingNumber";
import { formatCurrency } from "../../../../utils/format";

export default function CompensationSection({
  control,
  FloatingInput,
  getValues,
  setValue,
  cleanNumber,
  compensationArray,
  handleSellerCalculation,
  totalClosingMeta,
}) {
  const baseParent = 'compensationMeta';

  const data =
    Array.isArray(compensationArray?.fields) && compensationArray.fields.length
      ? compensationArray.fields
      : null;

  // Compute and set total
  const computeAndSetTotal = useCallback(() => {
    try {
      if (typeof getValues !== "function" || typeof setValue !== "function")
        return 0;
      const arr = getValues(baseParent) || [];
      let sum = 0;
      if (Array.isArray(arr)) {
        for (const it of arr) {
          if (!it) continue;
          const raw = it.value ?? it.amount ?? "0";
          const num = Number(String(raw).replace(/[^0-9.-]+/g, "")) || 0;
          sum += num;
        }
      }
      const formatted = formatCurrency(sum);
      try {
        setValue(`${baseParent}Total`, formatted, {
          shouldDirty: true,
          shouldTouch: false,
        });
      } catch (e) {
        /* ignore */
      }
      return sum;
    } catch (e) {
      return 0;
    }
  }, [getValues, setValue, baseParent]);

  // Watch for changes to compensationMeta array and recalculate total
  useEffect(() => {
    if (compensationArray?.fields && Array.isArray(compensationArray.fields)) {
      computeAndSetTotal();
    }
  }, [compensationArray?.fields, computeAndSetTotal]);

  // When a rate is formatted, recalc the dollar amount for that index
  const onRateFormatted = (origIndex) => {
    try {
      if (typeof getValues !== 'function' || typeof setValue !== 'function') return;
      //console.log('Recalculating compensation amount for index:', origIndex);
      const ratePath = `${baseParent}[${origIndex}].rate`;
      const rateRaw = getValues(ratePath);
      const salesRaw = getValues('salesPrice') || '0';
      const sales = Number(String(salesRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const rate = Number(String(rateRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const amount = Number.isFinite(sales * (rate / 100)) ? sales * (rate / 100) : 0;
      const formatted = formatCurrency(amount);
      setValue(`${baseParent}[${origIndex}].value`, formatted, { shouldDirty: true, shouldValidate: false });
      // update total compensation
      computeAndSetTotal();
      handleSellerCalculation && handleSellerCalculation();
    } catch (e) {
      // ignore
    }
  };

  // When an amount/value is formatted, recalc the rate for that index
  const onAmountFormatted = (origIndex) => {
    try {
      if (typeof getValues !== 'function' || typeof setValue !== 'function') return;
      const valuePath = `${baseParent}[${origIndex}].value`;
      const ratePath = `${baseParent}[${origIndex}].rate`;
      const valueRaw = getValues(valuePath) || '0';
      const salesRaw = getValues('salesPrice') || '0';
      const sales = Number(String(salesRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const amount = Number(String(valueRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const rate = sales > 0 ? (amount / sales) * 100 : 0;
      // round to 6 decimal places max then trim trailing zeros when formatted by consumer
      const rateRounded = Math.round((rate + Number.EPSILON) * 1000000) / 1000000;
      setValue(ratePath, rateRounded, { shouldDirty: true, shouldValidate: false });
      // update total compensation
      computeAndSetTotal();
      handleSellerCalculation && handleSellerCalculation();
    } catch (e) {
      // ignore
    }
  };


  if (!data) return null;

  return (
    <>
      <h3 className="m-btm-16">Compensation</h3>

      {/* Compensation Fields Grid */}
      <DynamicFieldsGrid
        fields={data}
        control={control}
        FloatingInput={FloatingInput}
        cleanNumber={cleanNumber}
        renderItem={(item, ctx) =>
          renderPaymentItem(item, {
            ...ctx,
            index: ctx.index,
            parentName: baseParent,
            onFormatted: (name, formatted) => {
              try {
                if (!name || typeof name !== 'string') return;
                const m = name.match(/\[(\d+)\]/);
                const idx = m ? Number(m[1]) : ctx.index;
                // determine whether the formatted field was rate or value and call appropriate handler
                if (name.endsWith('.rate')) {
                  onRateFormatted(idx);
                } else if (name.endsWith('.value') || name.endsWith('.amount')) {
                  onAmountFormatted(idx);
                } else {
                  // default: recalc amount from rate
                  onRateFormatted(idx);
                }
              } catch (e) {
                // ignore
              }
            }
          })
        }
        fullRowPerItem={true}
        parentName={baseParent}
        getRowClass={(pair) => {
          if (
            Array.isArray(pair) &&
            pair.some(
              (it) => it.day !== undefined && it.day !== null && it.day !== ""
            )
          )
            return "calc-frm-field symbol-wrap";
          return "calc-frm-field full-dvd";
        }}
        getValues={getValues}
        setValue={setValue}
      />

      {/* Total Compensation */}
      <div className="calc-frm-field full-dvd">
        <div className="calc-frm-fld form-group">
          <label className="lbl-section txt-bold">Total Compensation</label>
        </div>

        <div className="calc-frm-fld form-group sign-of-amount">
          <ControlledField
            name={`${baseParent}Total`}
            control={control}
            defaultValue={formatCurrency(totalClosingMeta.compensationMetaTotal) || "0.00"}
            InputComponent={FloatingInput || FloatingNumber}
            placeholder=""
            type="text"
            cleanNumber={cleanNumber}
            disabled={true}
            onFormatted={() => {
              /* noop */
            }}
            inputProps={{ disabled: true }}
          />
        </div>
      </div>

    </>
  );
}
